/**
 * Persist Keystone write-back rejection reasons across page refreshes.
 *
 * The clerk's "which invoices got rejected and why" state only lives in
 * KeyScan's frontend memory — the backend has no idea Keystone said no,
 * because the submit is a direct POST from backend to Keystone with no
 * long-term record on our side yet. Without a persistence layer, a browser
 * refresh would drop every errored invoice back into Done and the clerk
 * would lose track of which ones still need attention.
 *
 * Scope is deliberately narrow: localStorage, per-browser, keyed by the
 * DB invoice id. When Keystone or our local validation rejects an invoice
 * we record the message here. When a resubmit succeeds we clear it. On
 * every loadOldInvoices call we read the map and apply the status back.
 *
 * Not a permanent home: eventually this belongs on the backend as a
 * column on invoices (last_submit_status / last_submit_error), so it
 * survives a browser swap and surfaces in admin tooling. Noted for later.
 */

const ERRORS_KEY = 'ks-keystone-submit-errors';
const SUCCESS_KEY = 'ks-keystone-submit-successes';

type ErrorMap = Record<string, string>;
export interface SubmitSuccess {
  submittedAt: string;   // ISO timestamp
  resultKey?: string;    // Keystone's returned result_key
  pdfAttached?: boolean;
}
type SuccessMap = Record<string, SubmitSuccess>;

function readJson<T>(key: string): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {} as T;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as T) : ({} as T);
  } catch {
    return {} as T;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota or private-mode; nothing we can do */
  }
}

// ---- Errors ----

export function getSubmitError(invoiceId: number | null | undefined): string | undefined {
  if (invoiceId == null) return undefined;
  return readJson<ErrorMap>(ERRORS_KEY)[String(invoiceId)];
}

export function getAllSubmitErrors(): ErrorMap {
  return readJson<ErrorMap>(ERRORS_KEY);
}

export function setSubmitError(invoiceId: number | null | undefined, message: string): void {
  if (invoiceId == null) return;
  const map = readJson<ErrorMap>(ERRORS_KEY);
  map[String(invoiceId)] = message;
  writeJson(ERRORS_KEY, map);
}

export function clearSubmitError(invoiceId: number | null | undefined): void {
  if (invoiceId == null) return;
  const map = readJson<ErrorMap>(ERRORS_KEY);
  if (map[String(invoiceId)]) {
    delete map[String(invoiceId)];
    writeJson(ERRORS_KEY, map);
  }
}

// ---- Successes ----

export function getSubmitSuccess(invoiceId: number | null | undefined): SubmitSuccess | undefined {
  if (invoiceId == null) return undefined;
  return readJson<SuccessMap>(SUCCESS_KEY)[String(invoiceId)];
}

export function getAllSubmitSuccesses(): SuccessMap {
  return readJson<SuccessMap>(SUCCESS_KEY);
}

export function setSubmitSuccess(invoiceId: number | null | undefined, value: SubmitSuccess): void {
  if (invoiceId == null) return;
  const map = readJson<SuccessMap>(SUCCESS_KEY);
  map[String(invoiceId)] = value;
  writeJson(SUCCESS_KEY, map);
}

export function clearSubmitSuccess(invoiceId: number | null | undefined): void {
  if (invoiceId == null) return;
  const map = readJson<SuccessMap>(SUCCESS_KEY);
  if (map[String(invoiceId)]) {
    delete map[String(invoiceId)];
    writeJson(SUCCESS_KEY, map);
  }
}
